import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Grid,
  useTheme,
  alpha
} from '@mui/material';
import axios from 'axios';
import {
  Assessment as AssessmentIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { 
  postABCDE, 
  getABCDE, 
  updateABCDE, 
  ABCDEInput, 
  ABCDEResult, 
  getAnnotationDetails, 
  AnnotationDetails, 
  runSegmentationAnalysis, 
  getSegmentationForAnnotation, 
  deleteSegmentation,
  SegmentationResult 
} from '../../../services/abcdeAPI';
import ComparisonModal from './components/ComparisonModal';

// Import new components
import ABCDEHeader from './components/ABCDEHeader';
import ImageViewer from './components/ImageViewer';
import ABCDEForm from './components/ABCDEForm';
import SegmentationResults from './components/SegmentationResults';
import AssessmentHistory from './components/AssessmentHistory';
import AnnotationInfoPanel from './components/AnnotationInfoPanel';

interface ABCDESpaceProps {
  annotationId?: string;
  imageUrl?: string;
}

interface ComparisonData {
  current: {
    id: string;
    assessment: {
      asymmetry: number;
      borders: number;
      color: number;
      diameter: number;
      evolution: number;
      totalScore: number;
      notes?: string;
      recommendation?: string;
    };
    imageUrl?: string;
    createdAt?: string;
    annotationDetails?: {
      label: string;
      x: number;
      y: number;
      confidence?: number;
    };
  };
  historical: {
    id: string;
    assessment: {
      asymmetry: number;
      borders: number;
      color: number;
      diameter: number;
      evolution: number;
      totalScore: number;
      notes?: string;
      recommendation?: string;
    };
    imageUrl?: string;
    createdAt?: string;
    annotationDetails?: {
      label: string;
      x: number;
      y: number;
      confidence?: number;
    };
  };
}

const ABCDESpace: React.FC<ABCDESpaceProps> = ({ annotationId: propAnnotationId, imageUrl }) => {
  const { annotationId: routeAnnotationId } = useParams();
  const annotationId = propAnnotationId || routeAnnotationId || '';
  const navigate = useNavigate();
  const theme = useTheme();

  const [formData, setFormData] = useState<ABCDEInput>({
    annotationId: annotationId,
    asymmetry: 0,
    borders: 0,
    color: 0,
    diameter: 0,
    evolution: 0,
    notes: '',
    recommendation: ''
  });

  const [annotationDetails, setAnnotationDetails] = useState<AnnotationDetails | null>(null);
  const [existingResults, setExistingResults] = useState<ABCDEResult[]>([]);
  const [totalScore, setTotalScore] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingDetails, setLoadingDetails] = useState<boolean>(false);
  const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);
  const [currentAssessmentId, setCurrentAssessmentId] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [segmentationResults, setSegmentationResults] = useState<SegmentationResult[]>([]);
  const [segmentationLoading, setSegmentationLoading] = useState<boolean>(false);
  const [showSegmentation, setShowSegmentation] = useState<boolean>(false);
  const [comparisonModalOpen, setComparisonModalOpen] = useState<boolean>(false);
  const [comparisonData, setComparisonData] = useState<ComparisonData | null>(null);

  // ABCDE criteria descriptions
  const criteria = {
    asymmetry: {
      title: 'Asymmetry',
      description: 'One half of the mole does not match the other half',
      color: theme.palette.primary.main,
      options: [
        { value: 0, label: 'Symmetric', points: '0 points', description: 'Both halves match when folded' },
        { value: 1, label: 'Asymmetric in 1 axis', points: '1 point', description: 'Asymmetric in one direction' },
        { value: 2, label: 'Asymmetric in 2 axes', points: '2 points', description: 'Asymmetric in both directions' }
      ]
    },
    borders: {
      title: 'Borders',
      description: 'Edges are irregular, ragged, notched, or blurred',
      color: theme.palette.secondary.main,
      options: [
        { value: 0, label: 'Smooth, regular borders', points: '0 points', description: 'Clear, well-defined edges' },
        { value: 1, label: 'Irregular borders', points: '1 point', description: 'Ragged, notched, or blurred edges' }
      ]
    },
    color: {
      title: 'Color',
      description: 'Color varies from one area to another',
      color: theme.palette.warning.main,
      options: [
        { value: 0, label: 'Uniform color', points: '0 points', description: 'Single, consistent color throughout' },
        { value: 1, label: '2 colors', points: '1 point', description: 'Two distinct colors present' },
        { value: 2, label: '3+ colors', points: '2 points', description: 'Three or more colors present' }
      ]
    },
    diameter: {
      title: 'Diameter',
      description: 'Melanomas are usually larger than 6mm',
      color: theme.palette.info.main,
      options: [
        { value: 0, label: '< 6mm', points: '0 points', description: 'Smaller than a pencil eraser' },
        { value: 1, label: '≥ 6mm', points: '1 point', description: 'Larger than a pencil eraser' }
      ]
    },
    evolution: {
      title: 'Evolution',
      description: 'Any change in size, shape, color, elevation, or new symptoms',
      color: theme.palette.error.main,
      options: [
        { value: 0, label: 'No change', points: '0 points', description: 'Stable appearance over time' },
        { value: 1, label: 'Recent changes', points: '1 point', description: 'Any documented changes' }
      ]
    }
  };

  useEffect(() => {
    if (annotationId) {
      setFormData(prev => ({ ...prev, annotationId }));
      loadAnnotationDetails();
      loadExistingResults();
      loadSegmentationResults();
    }
  }, [annotationId]);

  useEffect(() => {
    const score = formData.asymmetry + formData.borders + formData.color + formData.diameter + formData.evolution;
    setTotalScore(score);
  }, [formData.asymmetry, formData.borders, formData.color, formData.diameter, formData.evolution]);

  const loadAnnotationDetails = async () => {
    setLoadingDetails(true);
    try {
      const details = await getAnnotationDetails(annotationId);
      setAnnotationDetails(details);
    } catch (error) {
      console.error('Failed to load annotation details:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const loadExistingResults = async () => {
    try {
      console.log(`Loading ABCDE results for annotation: ${annotationId}`);
      const results = await getABCDE(annotationId);
      console.log('ABCDE results received:', results);
      
      setExistingResults(results);
      if (results.length > 0) {
        const latest = results[0];
        console.log('Loading latest assessment:', latest);
        
        setCurrentAssessmentId(latest._additional.id);
        setIsUpdating(true);
        
        const newFormData = {
          annotationId: annotationId,
          asymmetry: latest.asymmetry,
          borders: latest.borders,
          color: latest.color,
          diameter: latest.diameter,
          evolution: latest.evolution,
          notes: latest.notes || '',
          recommendation: latest.recommendation || ''
        };
        
        console.log('Setting form data:', newFormData);
        setFormData(newFormData);
      } else {
        console.log('No existing results found, resetting form');
        setCurrentAssessmentId(null);
        setIsUpdating(false);
        setFormData({
          annotationId: annotationId,
          asymmetry: 0,
          borders: 0,
          color: 0,
          diameter: 0,
          evolution: 0,
          notes: '',
          recommendation: ''
        });
      }
    } catch (error) {
      console.error('Failed to load existing ABCDE results:', error);
      // Reset form on error
      setCurrentAssessmentId(null);
      setIsUpdating(false);
      setFormData({
        annotationId: annotationId,
        asymmetry: 0,
        borders: 0,
        color: 0,
        diameter: 0,
        evolution: 0,
        notes: '',
        recommendation: ''
      });
    }
  };

  const loadSegmentationResults = async () => {
    try {
      console.log(`Loading segmentation results for annotation: ${annotationId}`);
      const results = await getSegmentationForAnnotation(annotationId);
      console.log('Segmentation results received:', results);
      console.log('Number of results:', results.length);
      
      setSegmentationResults(results);
      
      // Auto-show segmentation if results exist
      if (results.length > 0) {
        setShowSegmentation(true);
        console.log('Auto-showing segmentation results');
      }
    } catch (error) {
      console.error('Failed to load segmentation results:', error);
    }
  };

  const handleInputChange = (field: keyof ABCDEInput, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setSubmitSuccess(false);

    try {
      console.log('Submitting ABCDE assessment...');
      console.log('Form data:', formData);
      console.log('Is updating:', isUpdating);
      console.log('Current assessment ID:', currentAssessmentId);
      
      let result;
      if (isUpdating && currentAssessmentId) {
        console.log(`Updating assessment with ID: ${currentAssessmentId}`);
        result = await updateABCDE(currentAssessmentId, formData);
      } else {
        console.log('Creating new assessment');
        result = await postABCDE(formData);
      }
      
      console.log('Submit result:', result);
      
      if (result.success) {
        setSubmitSuccess(true);
        await loadExistingResults(); // Reload to get updated data
        setTimeout(() => setSubmitSuccess(false), 3000);
      }
    } catch (error) {
      console.error('Failed to submit ABCDE assessment:', error);
      // Show error to user
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        console.error('Assessment not found for update, switching to create mode');
        setIsUpdating(false);
        setCurrentAssessmentId(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSegmentation = async (segmentationId: string) => {
    if (!window.confirm('Are you sure you want to delete this segmentation analysis? This will remove all generated images.')) {
      return;
    }

    try {
      console.log('Deleting segmentation:', segmentationId);
      const result = await deleteSegmentation(segmentationId);
      
      if (result.success) {
        console.log('Segmentation deleted successfully');
        // Reload segmentation results
        await loadSegmentationResults();
        alert('Segmentation analysis deleted successfully');
      } else {
        console.error('Failed to delete segmentation');
        alert('Failed to delete segmentation analysis');
      }
    } catch (error) {
      console.error('Error deleting segmentation:', error);
      
      let errorMessage = 'Failed to delete segmentation analysis.';
      if (axios.isAxiosError(error)) {
        if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response?.status === 403) {
          errorMessage = 'Not authorized to delete this analysis.';
        } else if (error.response?.status === 404) {
          errorMessage = 'Segmentation analysis not found.';
        }
      }
      
      alert(errorMessage);
    }
  };

  const handleRunSegmentation = async () => {
    if (!annotationDetails?.linkedMicroUrl && !annotationDetails?.macroImageUrl) {
      console.error('No image available for segmentation');
      alert('No image available for segmentation analysis');
      return;
    }

    setSegmentationLoading(true);
    try {
      // Use the available image (prefer micro, fallback to macro)
      const imagePath = annotationDetails.linkedMicroUrl || annotationDetails.macroImageUrl || '';
      
      // Remove the leading slash and convert URL back to file path
      const cleanImagePath = imagePath.startsWith('/') ? imagePath.substring(1) : imagePath;
      
      console.log('Running segmentation analysis for:', cleanImagePath);
      console.log('Annotation details:', annotationDetails);
      
      const result = await runSegmentationAnalysis(
        annotationId, 
        'patient-id', // You might want to get this from context or annotation details
        cleanImagePath
      );
      
      console.log('Segmentation result:', result);
      
      if (result.success) {
        console.log('Segmentation completed successfully:', result);
        
        // Wait a moment then reload segmentation results
        setTimeout(async () => {
          await loadSegmentationResults();
          setShowSegmentation(true);
          setSegmentationLoading(false);
        }, 1000);
        
        // Don't set loading to false here since we're waiting for reload
        return;
      } else {
        console.error('Segmentation failed:', result);
        alert('Segmentation analysis failed. Please try again.');
      }
    } catch (error) {
      console.error('Failed to run segmentation analysis:', error);
      
      let errorMessage = 'Failed to run segmentation analysis.';
      if (axios.isAxiosError(error)) {
        if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response?.status === 400) {
          errorMessage = 'Image file not found or invalid format.';
        } else if (error.response?.status === 500) {
          errorMessage = 'Server error during segmentation analysis.';
        }
      }
      
      alert(errorMessage);
    }
    
    // Only set loading to false if there was an error or unsuccessful result
    setSegmentationLoading(false);
  };

  const handleEditAssessment = (result: ABCDEResult) => {
    setCurrentAssessmentId(result._additional.id);
    setIsUpdating(true);
    setFormData({
      annotationId: annotationId,
      asymmetry: result.asymmetry,
      borders: result.borders,
      color: result.color,
      diameter: result.diameter,
      evolution: result.evolution,
      notes: result.notes || '',
      recommendation: result.recommendation || ''
    });
  };

  const handleNavigateToAssessment = (targetAnnotationId: string) => {
    navigate(`/abcde/${targetAnnotationId}`);
  };

  const handleCompareAssessments = (currentResult: ABCDEResult, historicalAssessment: any) => {
    const comparison: ComparisonData = {
      current: {
        id: currentResult._additional.id,
        assessment: {
          asymmetry: currentResult.asymmetry,
          borders: currentResult.borders,
          color: currentResult.color,
          diameter: currentResult.diameter,
          evolution: currentResult.evolution,
          totalScore: currentResult.totalScore,
          notes: currentResult.notes || '',
          recommendation: currentResult.recommendation || ''
        },
        imageUrl: currentResult.annotationDetails?.linkedMicroUrl || currentResult.annotationDetails?.macroImageUrl,
        createdAt: currentResult.createdAt,
        annotationDetails: currentResult.annotationDetails ? {
          label: currentResult.annotationDetails.label,
          x: currentResult.annotationDetails.x,
          y: currentResult.annotationDetails.y,
          confidence: currentResult.annotationDetails.confidence
        } : undefined
      },
      historical: {
        id: historicalAssessment.annotationId,
        assessment: historicalAssessment.assessment,
        imageUrl: historicalAssessment.imageUrl,
        createdAt: historicalAssessment.createdAt,
        annotationDetails: historicalAssessment.annotationDetails ? {
          label: historicalAssessment.annotationDetails.label,
          x: historicalAssessment.annotationDetails.x,
          y: historicalAssessment.annotationDetails.y,
          confidence: historicalAssessment.annotationDetails.confidence
        } : undefined
      }
    };
    
    setComparisonData(comparison);
    setComparisonModalOpen(true);
  };

  const displayImage = imageUrl || annotationDetails?.linkedMicroUrl || annotationDetails?.macroImageUrl;

  if (!annotationId) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <AssessmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              No Annotation Selected
            </Typography>
            <Typography color="text.secondary" paragraph>
              Please select an annotation to perform ABCDE analysis
            </Typography>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/molemapping')}
            >
              Go to Mole Mapping
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>
      {/* Header Component */}
      <ABCDEHeader
        annotationId={annotationId}
        annotationDetails={annotationDetails}
        isUpdating={isUpdating}
        existingResultsCount={existingResults.length}
        onBack={() => navigate(-1)}
        loadingDetails={loadingDetails}
      />

      <Grid container spacing={3}>
        {/* Left Column: Image and Info */}
        <Grid item xs={12} lg={6}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Image Viewer */}
            {displayImage && (
              <ImageViewer
                imageUrl={displayImage}
                altText="Lesion for ABCDE analysis"
                title="Lesion Image"
              />
            )}

            {/* Annotation Info Panel */}
            <AnnotationInfoPanel
              annotationId={annotationId}
              annotationDetails={annotationDetails}
              loading={loadingDetails}
            />
          </Box>
        </Grid>

        {/* Right Column: Assessment Form */}
        <Grid item xs={12} lg={6}>
          <ABCDEForm
            formData={formData}
            totalScore={totalScore}
            loading={loading}
            loadingDetails={loadingDetails}
            submitSuccess={submitSuccess}
            isUpdating={isUpdating}
            onInputChange={handleInputChange}
            onSubmit={handleSubmit}
          />
        </Grid>

        {/* Full Width: Segmentation Results */}
        <Grid item xs={12}>
          <SegmentationResults
            segmentationResults={segmentationResults}
            segmentationLoading={segmentationLoading}
            showSegmentation={showSegmentation}
            onToggleSegmentation={() => setShowSegmentation(!showSegmentation)}
            onRunSegmentation={handleRunSegmentation}
            onDeleteSegmentation={handleDeleteSegmentation}
          />
        </Grid>

        {/* Full Width: Assessment History */}
        {existingResults.length > 0 && (
          <Grid item xs={12}>
            <AssessmentHistory
              existingResults={existingResults}
              currentAssessmentId={currentAssessmentId}
              isUpdating={isUpdating}
              onEditAssessment={handleEditAssessment}
              onNavigateToAssessment={handleNavigateToAssessment}
              onCompareAssessments={handleCompareAssessments}
            />
          </Grid>
        )}
      </Grid>

      {/* Comparison Modal */}
      <ComparisonModal
        open={comparisonModalOpen}
        onClose={() => {
          setComparisonModalOpen(false);
          setComparisonData(null);
        }}
        comparisonData={comparisonData}
      />
    </Box>
  );
};

export default ABCDESpace;
